import React, { useState, useEffect } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

const AnalyticsTab = ({ user }) => {
    const [visits, setVisits] = useState(null);
    const [bot, setBot] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            adminFetch(`${API_URL}/api/admin/analytics/visits?days=${days}`).then(r => r.json()),
            adminFetch(`${API_URL}/api/admin/analytics/bot?days=${days}`).then(r => r.json()),
        ]).then(([v, b]) => {
            setVisits(v);
            setBot(b);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [days, user]);

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading analytics...</div>;

    const maxDaily = Math.max(...(visits?.dailyVisits || []).map(d => d.count), 1);

    return (
        <div style={{ padding: '0 0 40px' }}>
            {/* Period selector */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                {[7, 14, 30, 90].map(d => (
                    <button key={d} onClick={() => setDays(d)}
                        style={{
                            padding: '6px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: days === d ? '#7c5cbf' : '#f0f0f0',
                            color: days === d ? '#fff' : '#333', fontWeight: 600, fontSize: 13,
                        }}>
                        {d}d
                    </button>
                ))}
            </div>

            {/* ═══ SITE VISITS ═══ */}
            <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#333' }}>Site Visits</h3>

            {/* Summary cards */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                <SummaryCard label="Total Views" value={visits?.totalViews || 0} />
                <SummaryCard label="Unique Visitors" value={visits?.uniqueVisitors || 0} />
                <SummaryCard label="Avg/Day" value={visits?.dailyVisits?.length ? Math.round((visits.totalViews || 0) / visits.dailyVisits.length) : 0} />
            </div>

            {/* Daily bar chart */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#888' }}>Daily Views</h4>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120 }}>
                    {(visits?.dailyVisits || []).map(d => (
                        <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: '100%', maxWidth: 24, borderRadius: '4px 4px 0 0',
                                background: '#7c5cbf', height: `${Math.max((d.count / maxDaily) * 100, 4)}px`,
                            }} title={`${d.date}: ${d.count} views`} />
                        </div>
                    ))}
                </div>
                {(visits?.dailyVisits || []).length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#aaa' }}>
                        <span>{visits.dailyVisits[0].date.slice(5)}</span>
                        <span>{visits.dailyVisits[visits.dailyVisits.length - 1].date.slice(5)}</span>
                    </div>
                )}
            </div>

            {/* Top pages + Top users side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#888' }}>Top Pages</h4>
                    {(visits?.topPages || []).slice(0, 10).map(p => (
                        <div key={p.page} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
                            <span style={{ color: '#555', fontFamily: 'monospace' }}>{p.page}</span>
                            <span style={{ fontWeight: 600, color: '#7c5cbf' }}>{p.count}</span>
                        </div>
                    ))}
                    {(!visits?.topPages || visits.topPages.length === 0) && <div style={{ color: '#ccc', fontSize: 13 }}>No data yet</div>}
                </div>
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#888' }}>Top Users</h4>
                    {(visits?.topUsers || []).slice(0, 10).map((u, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
                            <span>{u.name} {u.username ? <span style={{ color: '#aaa' }}>@{u.username}</span> : ''}</span>
                            <span style={{ fontWeight: 600, color: '#7c5cbf' }}>{u.count}</span>
                        </div>
                    ))}
                    {(!visits?.topUsers || visits.topUsers.length === 0) && <div style={{ color: '#ccc', fontSize: 13 }}>No data yet</div>}
                </div>
            </div>

            {/* ═══ BOT ACTIVITY ═══ */}
            <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#333' }}>Bot Activity</h3>

            {/* Aggregate button stats (status + rating distributions over the period) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <BreakdownCard
                    title="Meeting Status — All Buttons Pressed"
                    items={[
                        { key: 'Met',       label: 'Met ✅',                  data: bot?.statusBreakdown?.Met },
                        { key: 'Scheduled', label: 'Scheduled 📆',            data: bot?.statusBreakdown?.Scheduled },
                        { key: 'No',        label: 'Not met ⭕️',              data: bot?.statusBreakdown?.No },
                        { key: 'Fail',      label: 'Something wrong 😔',      data: bot?.statusBreakdown?.Fail },
                        { key: 'Declined',  label: 'Decided not to meet 🤝',  data: bot?.statusBreakdown?.Declined },
                        { key: 'noResp',    label: 'No response ❔',           data: bot?.statusBreakdown?.noResponse, muted: true },
                    ]}
                />
                <BreakdownCard
                    title="Rating Distribution"
                    items={[
                        { key: '1', label: '😡 Awful',     data: bot?.ratingDistribution?.['1'], color: '#ef4444' },
                        { key: '2', label: '😐 Boring',    data: bot?.ratingDistribution?.['2'], color: '#f59e0b' },
                        { key: '3', label: '🙂 OK',        data: bot?.ratingDistribution?.['3'], color: '#3b82f6' },
                        { key: '4', label: '😃 Wonderful', data: bot?.ratingDistribution?.['4'], color: '#22c55e' },
                    ]}
                />
            </div>

            {/* Participation by week */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#888' }}>Weekly Participation (Yes/No)</h4>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #eee' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#888' }}>Week</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#22c55e' }}>Activated</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#ef4444' }}>Deactivated</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#888' }}>Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(bot?.participationByWeek || []).map(w => (
                            <tr key={w.week} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{w.week}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', color: '#22c55e', fontWeight: 600 }}>+{w.activated}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', color: '#ef4444', fontWeight: 600 }}>-{w.deactivated}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, color: w.activated - w.deactivated >= 0 ? '#22c55e' : '#ef4444' }}>
                                    {w.activated - w.deactivated >= 0 ? '+' : ''}{w.activated - w.deactivated}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {(!bot?.participationByWeek || bot.participationByWeek.length === 0) && <div style={{ color: '#ccc', fontSize: 13, padding: 8 }}>No data</div>}
            </div>

            {/* Feedback by week */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 14, color: '#888' }}>Weekly Match Feedback</h4>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 12 }}>
                    Each cell: <strong>count</strong> · % of responses that week · <span style={{ color: '#22c55e' }}>±Δ%</span> vs previous week
                </div>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #eee' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#888' }}>Week</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Matches</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#22c55e' }} title="Met ✅">Met</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#3b82f6' }} title="Scheduled 📆">Sched.</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#f59e0b' }} title="Not met ⭕️ (just didn't happen)">Not met</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#ef4444' }} title="Something went wrong 😔">Failed</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#a855f7' }} title="Decided not to meet 🤝">Declined</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#888' }}>No resp.</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Rated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(bot?.feedbackByWeek || []).map((w, idx, arr) => {
                            // Sorted ascending by week — prev is the row at idx-1.
                            const prev = idx > 0 ? arr[idx - 1] : null;
                            const denom = (w.total || 0) * 2; // 2 responses per match (member1 + member2)
                            const prevDenom = prev ? (prev.total || 0) * 2 : 0;
                            return (
                                <tr key={w.week} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{w.week}</td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>{w.total}</td>
                                    <PctCell value={w.met}              prevValue={prev?.met}              denom={denom} prevDenom={prevDenom} color="#22c55e" />
                                    <PctCell value={w.scheduled}        prevValue={prev?.scheduled}        denom={denom} prevDenom={prevDenom} color="#3b82f6" />
                                    <PctCell value={w.notMet || 0}      prevValue={prev?.notMet || 0}      denom={denom} prevDenom={prevDenom} color="#f59e0b" />
                                    <PctCell value={w.failed}           prevValue={prev?.failed}           denom={denom} prevDenom={prevDenom} color="#ef4444" />
                                    <PctCell value={w.declined || 0}    prevValue={prev?.declined || 0}    denom={denom} prevDenom={prevDenom} color="#a855f7" />
                                    <PctCell value={w.noResponse}       prevValue={prev?.noResponse}       denom={denom} prevDenom={prevDenom} color="#aaa" />
                                    <PctCell value={w.rated}            prevValue={prev?.rated}            denom={denom} prevDenom={prevDenom} color="#333" />
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {(!bot?.feedbackByWeek || bot.feedbackByWeek.length === 0) && <div style={{ color: '#ccc', fontSize: 13, padding: 8 }}>No data</div>}
            </div>

            {/* Script activity */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#888' }}>Bot Scripts Activity</h4>
                {(bot?.scriptActivity || []).map(s => (
                    <div key={s.script} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
                        <span style={{ fontFamily: 'monospace', color: '#555' }}>{s.script}</span>
                        <span>
                            <span style={{ color: '#22c55e', fontWeight: 600 }}>{s.sent} sent</span>
                            {s.failed > 0 && <span style={{ color: '#ef4444', fontWeight: 600, marginLeft: 12 }}>{s.failed} failed</span>}
                        </span>
                    </div>
                ))}
                {(!bot?.scriptActivity || bot.scriptActivity.length === 0) && <div style={{ color: '#ccc', fontSize: 13 }}>No data</div>}
            </div>
        </div>
    );
};

const SummaryCard = ({ label, value }) => (
    <div style={{ flex: 1, background: '#fff', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: '#333' }}>{value.toLocaleString()}</div>
    </div>
);

/**
 * Renders one cell with three lines of info:
 *   123          — absolute count (the button's primary metric)
 *   45%          — share of total responses that week (count / (matches*2))
 *   ▲ +3%        — week-over-week change in that share (this week's % minus prev week's %)
 *
 * Sign convention: delta is "is this number bigger or smaller than last week's same %?"
 * Color follows direction only, NOT goodness — a rising "Failed" % is still red-tinted up.
 * Interpretation is left to the reader (an admin tracking trends doesn't need the chart to moralize).
 */
const PctCell = ({ value, prevValue, denom, prevDenom, color }) => {
    const pct = denom > 0 ? Math.round((value / denom) * 100) : 0;
    const prevPct = prevDenom > 0 && prevValue !== undefined ? Math.round((prevValue / prevDenom) * 100) : null;
    const delta = prevPct !== null ? pct - prevPct : null;
    const deltaColor = delta === null ? '#ccc' : delta > 0 ? '#22c55e' : delta < 0 ? '#ef4444' : '#aaa';
    const arrow = delta === null ? '' : delta > 0 ? '▲' : delta < 0 ? '▼' : '·';
    const sign = delta === null ? '' : delta > 0 ? '+' : '';

    return (
        <td style={{ textAlign: 'right', padding: '6px 8px', color, lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600 }}>{value}</div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>{pct}%</div>
            {delta !== null && (
                <div style={{ fontSize: 10, color: deltaColor, fontWeight: 500 }}>
                    {arrow} {sign}{delta}%
                </div>
            )}
        </td>
    );
};

const BreakdownCard = ({ title, items }) => {
    const totalCount = items.reduce((acc, i) => acc + (i.data?.count || 0), 0);
    return (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#888' }}>
                {title} <span style={{ color: '#ccc', fontWeight: 400 }}>· {totalCount} total</span>
            </h4>
            {items.map(item => {
                const count = item.data?.count || 0;
                const pct = item.data?.percentage || 0;
                const barColor = item.color || (item.muted ? '#ccc' : '#7c5cbf');
                return (
                    <div key={item.key} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, color: item.muted ? '#888' : '#333' }}>
                            <span>{item.label}</span>
                            <span style={{ fontWeight: 600 }}>{count} <span style={{ color: '#aaa', fontWeight: 400 }}>· {pct}%</span></span>
                        </div>
                        <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: barColor, transition: 'width 0.3s' }} />
                        </div>
                    </div>
                );
            })}
            {totalCount === 0 && <div style={{ color: '#ccc', fontSize: 13, padding: '8px 0' }}>No data yet</div>}
        </div>
    );
};

export default AnalyticsTab;
